import { Component, HostListener, Inject } from "@angular/core";
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA as MAT_DIALOG_DATA } from "@angular/material/dialog";
import { Store } from "@ngxs/store";
import { Compass, CompassAccessControl, CompassAccessType, CompassAnswerable, CompassCopyControl, CompassPublicationType, CompassStatus, CompassTrackable, LicenseTier, PublishCompass, UpdateCopyControls, UpdateLicenseTiers, UpdateProtectedSubscribers } from "huddyapi";
import { DesktopErrorHandler } from "../../services/errorHandler.service";
import { Util } from "../util";

@Component({
  selector: 'publish-compass-dialog',
  templateUrl: 'publish-compass-dialog.component.html',
  styleUrls: ["./publish-compass-dialog.component.scss"]
})
export class PublishCompassDialog {
  set working(value: boolean) {
    this._working = value;
    this.dialogRef.disableClose = this.working;
  }

  get working(): boolean {
    return this._working;
  }

  _working: boolean;
  page: number;
  emailInputValue: string;
  licenseInputValue: LicenseTier;
  savedEmails: string[];
  savedCopyControls: CompassCopyControls;
  savedLicenseTiers: LicenseTier[];
  emails: string[];
  copyControls: CompassCopyControls;
  licenseTiers: LicenseTier[];
  publishType: CompassPublicationType;
  checkAllState: boolean;
  stringComparator: (a: string, b: string) => number = (a: string, b: string): number => a.toLocaleLowerCase().localeCompare(b.toLocaleLowerCase());

  constructor(
    private store: Store,
    private errorHandler: DesktopErrorHandler,
    public dialogRef: MatDialogRef<PublishCompassDialog>,
    public dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: PublishCompassDialogData) {
    this.working = false;
    this.checkAllState = true;
    this.emailInputValue = '';
    this.licenseInputValue = new LicenseTier();
    this.emails = [...(this.data.compass.protectedSubscribers || [])].sort(this.stringComparator);
    this.copyControls = new CompassCopyControls(this.data.compass);
    this.licenseTiers = [...(this.data.compass.licenseTiers || [])].sort(LicenseTier.compare);
    this.savedEmails = [...this.emails];
    this.savedCopyControls = this.copyControls.clone();
    this.savedLicenseTiers = [...this.licenseTiers];
    if(this.isPublished()) {
      this.page = this.isProtected() ? 1 : 2;
      this.publishType = this.data.compass.publicationType;
    } else {
      this.page = 0;
      this.publishType = null;
    }
  }

  isPublished(): boolean {
    return this.data.compass.status != CompassStatus.DRAFT;
  }

  isProtected(): boolean {
    return this.data.compass.publicationType == CompassPublicationType.PROTECTED;
  }

  getSettingTitle(): string {
    if(this.isPublished())
      return 'Published Compass Settings'
    switch(this.page) {
      case 1:
        return 'Add the emails that will have access to this compass';
      case 2:
        return 'Configure the copy controls for this compass';
      case 3:
        return 'Configure the price of bulk subscriptions to this compass';
      default:
        return '';
    }
  }

  canAddEmail(): boolean {
    return !this.working && Util.isEmail(this.emailInputValue);
  }
  
  addEmail() {
    if(!this.canAddEmail())
      return;
    const index: number = Util.sortedIndexOf(this.emails, this.emailInputValue, this.stringComparator);
    if(index < 0)
      this.emails.splice(index * -1 - 1, 0, this.emailInputValue);
    this.emailInputValue = '';
  }

  removeEmail(index: number) {
    this.emails.splice(index, 1);
  }

  canAddLicenseTier(): boolean {
    return !this.working && this.licenseInputValue.isValid();
  }

  addLicenseTier() {
    if(!this.canAddLicenseTier())
      return;
    const index: number = Util.sortedIndexOf(this.licenseTiers, this.licenseInputValue, LicenseTier.compare);
    if(index < 0)
      this.licenseTiers.splice(index * -1 - 1, 0, this.licenseInputValue.clone());
    else
      this.licenseTiers[index].price = this.licenseInputValue.price;
    this.licenseInputValue.clear();
  }

  removeLicenseTier(index: number) {
    this.licenseTiers.splice(index, 1);
  }

  getLicenseZeroText(): string[] {
    return LicenseTier.getText(1, this.licenseTiers.length > 0 ? this.licenseTiers[0].min - 1 : null, 0);
  }

  hasControlName(map: Map<CompassAccessControl, CompassCopyControl>, name: string): boolean {
    const accessControl: CompassAccessControl = CompassCopyControl.nameValueMap[name];
    return accessControl != null && map.has(accessControl);
  }

  setControlName(map: Map<CompassAccessControl, CompassCopyControl>, name: string, accessType: CompassAccessType, refId: number, value: boolean) {
    const accessControl: CompassAccessControl = CompassCopyControl.nameValueMap[name];
    if(accessControl == null)
      return
    if(value)
      map.delete(accessControl);
    else {
      map.set(accessControl, new CompassCopyControl({
        accessControl: accessControl,
        accessType: accessType,
        referenceId: refId
      }));
    }
  }

  emailsDirty(): boolean {
    return !Util.arraysEqual(this.emails, this.savedEmails);
  }

  copyControlsDirty(): boolean {
    return !this.copyControls.equals(this.savedCopyControls);
  }

  licenseTiersDirty(): boolean {
    return !Util.arraysEqual(this.licenseTiers, this.savedLicenseTiers, (a: LicenseTier, b: LicenseTier): boolean => a.equals(b));
  }

  canGoBack(): boolean {
    return !this.working && this.page > 0 && !this.isPublished();
  }

  canGoNext(type?: CompassPublicationType): boolean {
    if(this.working)
      return false;
    if(this.isPublished()) {
      let emailsDirty: boolean = false;
      if(this.isProtected()) {
        if(this.emails.length == 0)
          return false;
        emailsDirty = this.emailsDirty();
      }
      return emailsDirty || this.copyControlsDirty() || this.licenseTiersDirty();
    } else {
      if(this.page == 0 && this.data.compass.sourceId != 0 && type != CompassPublicationType.PRIVATE)
        return false;
      return this.page != 1 || this.emails.length > 0;
    }
  }

  setControlName_(map: Map<CompassAccessControl, CompassCopyControl>, name: string, accessType: CompassAccessType, refId: number, value: boolean) {
    const accessControl: CompassAccessControl = CompassCopyControl.nameValueMap[name];
    if(accessControl == null)
      return
    if(value)
      map.delete(accessControl);
    else {
      map.set(accessControl, new CompassCopyControl({
        accessControl: accessControl,
        accessType: accessType,
        referenceId: refId
      }));
    }
  }

  checkAllChanged() {
    this.setControlName(this.copyControls.compass, 'COMPASS_COPYABLE', CompassAccessType.COARSE_GRAINED, this.data.compass.id, this.checkAllState);
    this.setControlName(this.copyControls.compass, 'COMPASS_CAN_ADD_TRACKABLES', CompassAccessType.COARSE_GRAINED, this.data.compass.id, this.checkAllState);
    this.copyControls.trackables.forEach((controls: Map<CompassAccessControl, CompassCopyControl>, index: number) => {
      this.setControlName(controls, 'TRACKABLE_NAME_EDITING_ALLOWED', CompassAccessType.TRACKABLE, this.data.compass.trackables[index].trackableId, this.checkAllState);
      this.setControlName(controls, 'TRACKABLE_SCALE_EDITING_ALLOWED', CompassAccessType.TRACKABLE, this.data.compass.trackables[index].trackableId, this.checkAllState);
      this.setControlName(controls, 'TRACKABLE_DELETION_ALLOWED', CompassAccessType.TRACKABLE, this.data.compass.trackables[index].trackableId, this.checkAllState);
    });
    this.setControlName(this.copyControls.assessment, 'ASSESSMENT_CONCLUSION_EDITING_ALLOWED', CompassAccessType.ASSESSMENT, null, this.checkAllState);
    this.setControlName(this.copyControls.assessment, 'ASSESSMENT_ATTRIBUTION_EDITING_ALLOWED', CompassAccessType.ASSESSMENT, null, this.checkAllState);
    this.setControlName(this.copyControls.assessment, 'ASSESSMENT_REMINDER_EDITING_ALLOWED', CompassAccessType.ASSESSMENT, null, this.checkAllState);
    this.setControlName(this.copyControls.assessment, 'ASSESSMENT_DELETION_ALLOWED', CompassAccessType.ASSESSMENT, null, this.checkAllState);
    this.copyControls.questions.forEach((controls: Map<CompassAccessControl, CompassCopyControl>, index: number) => {
      this.setControlName(controls, 'ASSESSMENT_QUESTION_TEXT_EDITING_ALLOWED', CompassAccessType.ASSESSMENT_QUESTION, index, this.checkAllState);
      this.setControlName(controls, 'ASSESSMENT_QUESTION_ANSWER_EDITING_ALLOWED', CompassAccessType.ASSESSMENT_QUESTION, index, this.checkAllState);
      this.setControlName(controls, 'ASSESSMENT_QUESTION_SCALE_EDITING_ALLOWED', CompassAccessType.ASSESSMENT_QUESTION, index, this.checkAllState);
      this.setControlName(controls, 'ASSESSMENT_QUESTION_DELETION_ALLOWED', CompassAccessType.ASSESSMENT_QUESTION, index, this.checkAllState);
    });
    this.setControlName(this.copyControls.compass, 'SCORE_SUMMARY_TEXT_EDITING_ALLOWED', CompassAccessType.SCORE_SUMMARY, null, this.checkAllState);
    this.setControlName(this.copyControls.compass, 'SCORE_SUMMARY_SCALE_EDITING_ALLOWED', CompassAccessType.SCORE_SUMMARY, null, this.checkAllState);
  }

  goBack() {
    if(!this.canGoBack())
      return;
    if(this.page == 2)
      this.page = this.publishType == CompassPublicationType.PROTECTED ? 1 : 0;
    else {
      this.page = 0;
      this.publishType = null;
    }
  }

 

  publish() {
    this.working = true;
    this.store.dispatch(new PublishCompass(new Compass({
      ...this.data.compass,
      protectedSubscribers: this.emails,
      licenseTiers: this.licenseTiers,
      accessControl: Array.from(this.copyControls.compass),
      trackables: (this.data.compass.trackables || []).map((trackable: CompassTrackable, index: number): CompassTrackable => new CompassTrackable({
        ...trackable,
        accessControl: Array.from(this.copyControls.trackables[index])
      })),
      assessment: this.data.compass.assessment ? {
        ...this.data.compass.assessment,
        accessControl: Array.from(this.copyControls.assessment),
        questions: (this.data.compass.assessment.questions || []).map((question: CompassAnswerable, index: number): CompassAnswerable => new CompassAnswerable({
          ...question,
          accessControl: Array.from(this.copyControls.questions[index])
        }))
      } : this.data.compass.assessment
    }), CompassPublicationType.PUBLIC)).subscribe(() => this.dialogRef.close(), (error: any) => {
      this.working = false;
      this.errorHandler.handle(error, 'compass');
    });
  }

  async update() {
    this.working = true;
    let promises: Promise<any>[] = [];
    if(this.emailsDirty())
      promises.push(this.store.dispatch(new UpdateProtectedSubscribers(this.data.compass.id, this.emails)).toPromise().then(() => this.savedEmails = [...this.emails]));
    if(this.copyControlsDirty())
      promises.push(this.store.dispatch(new UpdateCopyControls(this.data.compass.id, this.copyControls.getList())).toPromise().then(() => this.copyControls = this.savedCopyControls.clone()));
    if(this.licenseTiersDirty())
      promises.push(this.store.dispatch(new UpdateLicenseTiers(this.data.compass.id, this.licenseTiers)).toPromise().then(() => this.savedLicenseTiers = [...this.licenseTiers]));
    Promise.all(promises).then(() => this.dialogRef.close()).catch((error: any) => {
      this.working = false;
      this.errorHandler.handleSimple(error, 'Error updating settings');
    });
  }

  @HostListener('window:keydown', ['$event'])
  handleKeydown(event: any) {
    if(this.dialog.openDialogs?.length > 0 && this.dialog.openDialogs[this.dialog.openDialogs.length - 1] != this.dialogRef)
      return;
    switch(event.key) {
      case 'ArrowUp':
        event.preventDefault();
        break;
      case 'ArrowDown':
        event.preventDefault();
        break;
      case 'Enter':
        switch(this.page) {
          case 1:
            event.preventDefault();
            // if(!this.working)
            //   this.emailInputValue?.length > 0 ? this.addEmail() : this.goNext();
            break;
          case 3:
            event.preventDefault();
            // if(!this.working)
            //   this.licenseInputValue.isClear() ? this.goNext() : this.addLicenseTier();
          default:
        }
      default:
    }
  }
}

export interface PublishCompassDialogData {
  compass: Compass;
}

export enum PublishCompassDialogUpdateType {
  COPY_CONTROLS, EMAILS
}

class CompassCopyControls {
  compass: Map<CompassAccessControl, CompassCopyControl>;
  trackables: Map<CompassAccessControl, CompassCopyControl>[];
  assessment: Map<CompassAccessControl, CompassCopyControl>;
  questions: Map<CompassAccessControl, CompassCopyControl>[];

  constructor(compass?: Compass) {
    this.compass = new Map<CompassAccessControl, CompassCopyControl>((compass?.accessControl || []).map((control: CompassCopyControl) => [control.accessControl, control.clone()]));
    this.trackables = (compass?.trackables || []).map((trackable: CompassTrackable): Map<CompassAccessControl, CompassCopyControl> => new Map<CompassAccessControl, CompassCopyControl>((trackable.accessControl || []).map((control: CompassCopyControl) => [control.accessControl, control.clone()])));
    this.assessment = new Map<CompassAccessControl, CompassCopyControl>((compass?.assessment?.accessControl || []).map((control: CompassCopyControl) => [control.accessControl, control.clone()]));
    this.questions = (compass?.assessment?.questions || []).map((question: CompassAnswerable): Map<CompassAccessControl, CompassCopyControl> => new Map<CompassAccessControl, CompassCopyControl>((question.accessControl || []).map((control: CompassCopyControl) => [control.accessControl, control.clone()])));
  }

  getList(): CompassCopyControl[] {
    let result: CompassCopyControl[] = [];
    result.push(...Array.from(this.compass.values()));
    this.trackables.forEach((controls: Map<CompassAccessControl, CompassCopyControl>) => result.push(...Array.from(controls.values())));
    result.push(...Array.from(this.assessment.values()));
    this.questions.forEach((controls: Map<CompassAccessControl, CompassCopyControl>) => result.push(...Array.from(controls.values())));
    return result;
  }

  equals(other: any): boolean {
    if(!(other instanceof CompassCopyControls))
      return false;
    return CompassCopyControls.controlMapsEqual(this.compass, other.compass)
      && CompassCopyControls.controlMapsEqual(this.assessment, other.assessment)
      && Util.arraysEqual(this.trackables, other.trackables, CompassCopyControls.controlMapsEqual)
      && Util.arraysEqual(this.questions, other.questions, CompassCopyControls.controlMapsEqual);
  }

  clone(): CompassCopyControls {
    let result: CompassCopyControls = new CompassCopyControls();
    result.compass = new Map<CompassAccessControl, CompassCopyControl>(Array.from(this.compass.values()).map((control: CompassCopyControl) => [control.accessControl, control.clone()]));
    result.trackables = this.trackables.map((controls: Map<CompassAccessControl, CompassCopyControl>): Map<CompassAccessControl, CompassCopyControl> => new Map<CompassAccessControl, CompassCopyControl>(Array.from(controls.values()).map((control: CompassCopyControl) => [control.accessControl, control.clone()])));
    result.assessment = new Map<CompassAccessControl, CompassCopyControl>(Array.from(this.assessment.values()).map((control: CompassCopyControl) => [control.accessControl, control.clone()]));
    result.questions = this.questions.map((controls: Map<CompassAccessControl, CompassCopyControl>): Map<CompassAccessControl, CompassCopyControl> => new Map<CompassAccessControl, CompassCopyControl>(Array.from(controls.values()).map((control: CompassCopyControl) => [control.accessControl, control.clone()])));
    return result;
  }

  private static controlMapsEqual(a: Map<CompassAccessControl, CompassCopyControl>, b: Map<CompassAccessControl, CompassCopyControl>): boolean {
    if(a.size != b.size)
      return false;
    return Array.from(a.values()).findIndex((aControl: CompassCopyControl): boolean => !aControl.equals(b.get(aControl.accessControl))) == -1;
  }
}